import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const setupBenefitsAnimation = (sectionRef, introRef) => {
  const section = sectionRef.current;
  const intro = introRef?.current;

  if (!section) {
    return () => {};
  }

  const cards = Array.from(
    section.querySelectorAll(".autobet-benefit-card")
  );

  if (!cards.length) {
    return () => {};
  }

  let ticking = false;

  // ---------------------------------------------------------
  // CARDS ANIMATION
  // ---------------------------------------------------------

  const updateCards = () => {
    if (ticking) {
      return;
    }

    ticking = true;

    requestAnimationFrame(() => {
      const viewportCenter = window.innerHeight * 0.55;
      const maxDist =
        window.innerWidth <= 600 ? 280 : 260;

      let closestCard = null;
      let closestDistance = Infinity;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const cardCenter =
          rect.top + rect.height / 2;

        const distance = Math.abs(
          cardCenter - viewportCenter
        );

        if (
          rect.bottom > 0 &&
          rect.top < window.innerHeight &&
          distance < closestDistance
        ) {
          closestDistance = distance;
          closestCard = card;
        }
      });

      // -----------------------------------------------------
      // MOBILE
      // -----------------------------------------------------

      if (window.innerWidth <= 700) {
        cards.forEach((card) => {
          card.classList.toggle(
            "is-active",
            card === closestCard
          );

          gsap.set(card, {
            clearProps: "all",
          });
        });

        ticking = false;
        return;
      }

      // -----------------------------------------------------
      // DESKTOP
      // -----------------------------------------------------

      cards.forEach((card, index) => {
        card.classList.toggle(
          "is-active",
          card === closestCard
        );

        const rect = card.getBoundingClientRect();

        const cardCenter =
          rect.top + rect.height / 2;

        const distanceFromCenter =
          cardCenter - viewportCenter;

        let normalized = Math.max(
          -2,
          Math.min(
            2,
            distanceFromCenter / maxDist
          )
        );

        /*
         * Keep the last card stable when it reaches
         * the center and the user continues scrolling.
         */
        if (
          index === cards.length - 1 &&
          distanceFromCenter < 0
        ) {
          normalized = 0;
        }

        const distance = Math.abs(normalized);

        let scale = 0.94;
        let opacity = 0.5;
        let rotateX = 0;
        let translateY = 0;

        if (distance <= 1) {
          scale = 1 - distance * 0.06;
          opacity = 1 - distance * 0.3;

          /*
           * Very subtle depth effect.
           */
          rotateX =
            -Math.sign(normalized) *
            (distance * 20);

          translateY = normalized * -5;
        } else {
          scale = 0.94;
          opacity = 0.65;

          rotateX =
            -Math.sign(normalized) *
            ((2 - distance) * 20);

          translateY =
            Math.sign(normalized) * -5;
        }

        gsap.to(card, {
          duration: 0.4,
          ease: "power2.out",
          scale,
          opacity,
          rotateX,
          y: translateY,
          transformPerspective: 1400,
          overwrite: "auto",
        });
      });

      ticking = false;
    });
  };

  // ---------------------------------------------------------
  // KEEP CARDS BELOW INTRO
  // ---------------------------------------------------------

  const updatePadding = () => {
    const cardsWrapper = section.querySelector(
      ".benefits-cards-wrapper"
    );

    if (cardsWrapper && intro) {
      cardsWrapper.style.paddingTop =
        `${intro.offsetHeight}px`;
    }
  };

  // ---------------------------------------------------------
  // KEEP INTRO HORIZONTALLY ALIGNED
  // ---------------------------------------------------------

  const updateIntroPosition = () => {
    if (!intro) {
      return;
    }

    /*
     * The intro itself is full width.
     *
     * We calculate the position of the actual MUI
     * Container inside it and preserve that geometry
     * when ScrollTrigger pins the intro.
     */

    const container = intro.querySelector(
      ".MuiContainer-root"
    );

    if (!container) {
      return;
    }

    const sectionRect =
      section.getBoundingClientRect();

    const containerRect =
      container.getBoundingClientRect();

    /*
     * Store the exact horizontal position of the
     * container relative to the viewport.
     */
    intro.dataset.containerLeft =
      containerRect.left;

    intro.dataset.containerWidth =
      containerRect.width;

    intro.dataset.sectionLeft =
      sectionRect.left;
  };

  // ---------------------------------------------------------
  // RESIZE
  // ---------------------------------------------------------

  const resizeHandler = () => {
    updateIntroPosition();
    updateCards();
    updatePadding();

    ScrollTrigger.refresh();
  };

  window.addEventListener(
    "scroll",
    updateCards,
    {
      passive: true,
    }
  );

  window.addEventListener(
    "resize",
    resizeHandler
  );

  // ---------------------------------------------------------
  // GSAP / SCROLLTRIGGER
  // ---------------------------------------------------------

  const ctx = gsap.context(() => {
    updatePadding();
    updateIntroPosition();

    if (intro) {
      ScrollTrigger.create({
        trigger: section,

        start: () =>
          window.innerWidth <= 700
            ? "top top+=75"
            : "top top+=60",

        end: "bottom center",

        pin: intro,

        pinSpacing: false,

        invalidateOnRefresh: true,

        onRefresh: () => {
          updateIntroPosition();
          updatePadding();
        },
      });
    }

    // -------------------------------------------------------
    // DESCRIPTION FADE
    // -------------------------------------------------------

    const description =
      section.querySelector(
        ".benefits-description"
      );

    if (description) {
      description.style.overflow = "hidden";
      
      const container = intro.querySelector(".MuiContainer-root");

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: () =>
            window.innerWidth <= 700
              ? "top top+=75"
              : "top top+=80",
          end: "+=200",
          scrub: true,
        },
      });

      tl.to(description, {
        opacity: 0,
        height: 0,
        paddingTop: 0,
        marginTop: 0,
        marginBottom: 0,
        ease: "none",
      }, 0);

      if (container) {
        tl.to(container, {
          paddingBottom: 20,
          ease: "none",
        }, 0);
      }
    }

    // Initial state
    updateCards();

    updatePadding();

    updateIntroPosition();

    ScrollTrigger.refresh();
  }, section);

  // ---------------------------------------------------------
  // CLEANUP
  // ---------------------------------------------------------

  return () => {
    window.removeEventListener(
      "scroll",
      updateCards
    );

    window.removeEventListener(
      "resize",
      resizeHandler
    );

    ctx.revert();
  };
};